import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Box,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { Integration } from '../../services/mocks/data/integrations';

interface DeleteIntegrationConfirmModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    integration: Integration | null;
    isDeleting: boolean;
}

const DeleteIntegrationConfirmModal: React.FC<DeleteIntegrationConfirmModalProps> = ({
    open,
    onClose,
    onConfirm,
    integration,
    isDeleting,
}) => {
    const { t } = useTranslation();

    if (!integration) return null;

    return (
        <Dialog
            open={open}
            onClose={!isDeleting ? onClose : undefined}
            aria-labelledby="delete-integration-dialog-title"
            aria-describedby="delete-integration-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="delete-integration-dialog-title">
                {t('integrations.deleteModal.title')}
            </DialogTitle>
            <DialogContent>
                <Box id="delete-integration-dialog-description">
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1">
                            {t('integrations.deleteModal.confirmText', { name: integration.name })}
                        </Typography>
                    </Box>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 'medium',
                            color: '#D97706', // Amber/yellow color
                            bgcolor: 'rgba(217, 119, 6, 0.1)', // Light amber background
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <WarningIcon 
                            fontSize="small" 
                            sx={{ 
                                mr: 1,
                                color: '#D97706'
                            }} 
                        />
                        {t('integrations.deleteModal.warningText')}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    color="inherit" 
                    disabled={isDeleting}
                >
                    {t('common.cancel')}
                </Button>
                <Button 
                    onClick={onConfirm} 
                    color="error" 
                    variant="contained" 
                    disabled={isDeleting}
                    autoFocus
                >
                    {isDeleting ? t('common.deleting') : t('common.delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteIntegrationConfirmModal;
